import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { isTopLevelDestination } from './destinations';

/**
 * **The phone's top bar — the brand, on every member screen** (`MEM-042`, `MEM-056`).
 *
 * ## Why it exists, and that neither design draws it
 *
 * Sumit, reviewing #526 on a phone width: *"On mobile view I think h2h branding is missing. Maybe
 * add logo at the top centered?"* — the design call left to us. Below `md` the desktop header is
 * hidden and the bottom bar carries icons and words, so nothing on a phone screen said H2HSpark.
 * Neither `Web App.dc.html` (no breakpoints at all) nor the phone reference draws this bar; it is
 * **a presentation addition**, and the pull request lists it as one.
 *
 * ## Every screen, since Sumit's ruling of 2026-09-11
 *
 * It was drawn on the four tab screens only, because a sub-page carried its own back control and
 * two headers on a 375px screen is one too many. Reviewing #529: *"the header disappears on event
 * detail / matches page."* So the bar is on every member screen, and the resolution to the two
 * headers is the other way round — **the bar carries the back control**, which is what
 * `Member App.dc.html`'s sub-page chrome draws: a 38px round back button, then the title. The
 * page's own link is `md` and up (`page-back-link.tsx`), so the action is offered once at each
 * width.
 *
 * ## What is on it
 *
 * A slim sticky bar, 52px beneath the status bar's safe area, on `--surface-card` with a hairline
 * `--border-subtle` rule — **kept white on Sumit's second thought**, 2026-09-11: asked about a
 * transparent bar he answered *"actually the white bar looks fine on the phone let's keep it for
 * now."* The logotype sits at 20px, centred, and opens Discover; on a sub-page a round back button
 * sits at the left, and a spacer of the same width sits at the right so the logo stays centred on
 * the screen rather than on what is left of it.
 *
 * **No other control** — no avatar, no bell, no search: the tab bar is the navigation, and a second
 * row of controls at the top of a one-handed screen is the thing the phone shell exists to avoid.
 *
 * ## Where back goes
 *
 * `navigate(-1)` — the browser's own history, which is what a round back button on a phone means
 * and the only thing that is right on a screen reached from three places. A screen opened from a
 * mailed link has no history to go back to, and the fallback is Discover rather than a button that
 * does nothing: `history.length` is the test, and it is read at the tap rather than at render so a
 * member who has since navigated gets the honest answer.
 *
 * ## Why it is its own file
 *
 * It appears below `md` and not above it, so `PLT-008` AC-3 requires it to be declared in
 * `responsive-behaviour.ts`, and that declaration is by file. Nothing becomes unreachable at either
 * width: above `md` the desktop header's logo does the same job, and the page's own back link is
 * the way back.
 */
export function PhoneTopBar({
  pathname,
}: {
  readonly pathname: string;
}): React.ReactElement {
  const navigate = useNavigate();
  const onTabScreen = isTopLevelDestination(pathname);

  return (
    <header className="sticky top-0 z-30 border-b border-border-subtle bg-card pt-[env(safe-area-inset-top)] md:hidden print:hidden">
      <div className="flex h-[52px] items-center justify-between px-3">
        {onTabScreen ? (
          <span className="h-[38px] w-[38px] flex-none" aria-hidden />
        ) : (
          <button
            type="button"
            aria-label="Back"
            onClick={() => {
              // One step of the member's own history, or Discover for a screen they arrived at
              // from a mailed link with nothing behind it.
              if (window.history.length > 1) navigate(-1);
              else navigate('/app');
            }}
            className="inline-flex h-[38px] w-[38px] flex-none items-center justify-center rounded-pill text-brand"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
        )}
        <Link to="/app" className="flex items-center" aria-label="H2HSpark — Discover">
          <img
            src="/brand/logotype.png"
            alt=""
            aria-hidden
            className="h-5 w-auto"
            width={1787}
            height={298}
          />
        </Link>
        {/* The logo is centred on the **screen**, so the right keeps the left's width whether or
            not there is a back button in it. */}
        <span className="h-[38px] w-[38px] flex-none" aria-hidden />
      </div>
    </header>
  );
}
